import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getLatestDocuments,
  getDocumentCandidates,
  getDocumentFindings,
  triggerDocumentScan,
} from "../api/documents.js";
import type {
  DocumentRunSummary,
  DocumentCandidateSummary,
  DocumentFinding,
} from "../api/product-types.js";

export function useLatestDocuments(websiteId: string | undefined) {
  return useQuery<DocumentRunSummary | null>({
    queryKey: ["documents", websiteId],
    queryFn: () => {
      if (!websiteId) throw new Error("websiteId is required");
      return getLatestDocuments(websiteId);
    },
    enabled: websiteId != null,
  });
}

export function useDocumentCandidates(
  websiteId: string | undefined,
  runId: string | undefined,
) {
  return useQuery<DocumentCandidateSummary[]>({
    queryKey: ["document-candidates", websiteId, runId],
    queryFn: () => {
      if (!websiteId) throw new Error("websiteId is required");
      if (!runId) throw new Error("runId is required");
      return getDocumentCandidates(websiteId, runId);
    },
    enabled: websiteId != null && runId != null,
  });
}

export function useDocumentFindings(
  websiteId: string | undefined,
  runId: string | undefined,
) {
  return useQuery<DocumentFinding[]>({
    queryKey: ["document-findings", websiteId, runId],
    queryFn: () => {
      if (!websiteId) throw new Error("websiteId is required");
      if (!runId) throw new Error("runId is required");
      return getDocumentFindings(websiteId, runId);
    },
    enabled: websiteId != null && runId != null,
  });
}

export function useTriggerDocumentScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ websiteId }: { websiteId: string }) =>
      triggerDocumentScan(websiteId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ["documents", variables.websiteId],
      });
    },
  });
}
